import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

// The player-name box with the dropdown of previously searched names, shared by the Player filter and
// the home band. Presentational: the owner supplies the names and decides what commit, select and
// remove mean. Autofill is off on purpose: a field that looks like a username makes Chrome offer saved
// passwords over the list.
export interface PlayerNameComboboxProps {
  value: string;
  onChange: (value: string) => void;
  // Enter with nothing highlighted, or blur. The owner decides whether it means anything.
  onCommit: (value: string) => void;
  // A saved name picked from the list.
  onSelect: (name: string) => void;
  onRemove?: (name: string) => void;
  names: string[];
  // The committed value. While the box still shows it, the whole list is offered; otherwise it narrows
  // to what's typed.
  committed?: string | null;
  placeholder?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  // Rendered inside the container, after the input (the filter's clear button).
  children?: React.ReactNode;
  className?: string;
}

export default function PlayerNameCombobox({
  value,
  onChange,
  onCommit,
  onSelect,
  onRemove,
  names,
  committed,
  placeholder,
  inputProps,
  children,
  className,
}: PlayerNameComboboxProps): React.ReactNode {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const query = value.trim().toLowerCase();
  const showAll = query === '' || query === (committed ?? '').toLowerCase();
  const suggestions = useMemo(
    () => (showAll ? names : names.filter((n) => n.toLowerCase().includes(query))),
    [names, showAll, query],
  );
  const dropdownVisible = open && suggestions.length > 0;

  // Close on any outside click. Bound only while open.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const select = (name: string) => {
    setOpen(false);
    setHighlight(-1);
    onSelect(name);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key == 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (event.key == 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key == 'Enter') {
      if (dropdownVisible && highlight >= 0 && highlight < suggestions.length) {
        event.preventDefault();
        select(suggestions[highlight]);
      } else {
        // Inside a real <form> (the home band) Enter submits it, so only swallow the key without a form.
        if (!(event.currentTarget.form)) {
          event.preventDefault();
        }
        setOpen(false);
        setHighlight(-1);
        onCommit(value);
      }
    } else if (event.key == 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
    inputProps?.onKeyDown?.(event);
  };

  return (
    <div
      className={`cm-player-filter${className ? ` ${className}` : ''}`}
      style={{ position: 'relative' }}
      ref={containerRef}
    >
      <input
        {...inputProps}
        className={`form-control${inputProps?.className ? ` ${inputProps.className}` : ''}`}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
          setHighlight(-1);
        }}
        onFocus={(event) => {
          setOpen(true);
          inputProps?.onFocus?.(event);
        }}
        onBlur={(event) => {
          setOpen(false);
          setHighlight(-1);
          onCommit(value);
          inputProps?.onBlur?.(event);
        }}
        onKeyDown={onKeyDown}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        role="combobox"
        aria-expanded={dropdownVisible}
        aria-autocomplete="list"
        data-lpignore="true"
        data-1p-ignore="true"
      />
      {children}
      {dropdownVisible ? (
        <ul className="cm-player-menu" role="listbox">
          {suggestions.map((name, i) => (
            <li
              key={name}
              role="option"
              aria-selected={i === highlight}
              className={`cm-player-option${i === highlight ? ' cm-player-option-active' : ''}`}
              // preventDefault keeps focus on the input so it doesn't blur before the click.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => select(name)}
            >
              <span className="cm-player-option-name">{name}</span>
              {onRemove ? (
                <button
                  type="button"
                  className="cm-player-option-remove"
                  title={`Remove ${name}`}
                  aria-label={`Remove ${name}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(name);
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
